<template>
  <div class="question-bank-page">
    <el-card class="filter-card">
      <div slot="header" class="card-header">
        <div class="card-header-left"><i class="el-icon-collection"></i><span>题库中心</span>
          <el-divider direction="vertical"/><span class="header-sub">知识库题目管理 / AI诊断 / 去重</span></div>
      </div>
      <el-form :inline="true" :model="query" class="filter-form">
        <el-form-item label="题型">
          <el-select v-model="query.questionType" clearable placeholder="全部题型" style="width:150px">
            <el-option v-for="(name,val) in QUESTION_TYPE_MAP" :key="val" :label="name" :value="Number(val)"/>
          </el-select>
        </el-form-item>
        <el-form-item label="诊断状态">
          <el-select v-model="query.reviewStatus" clearable placeholder="全部" style="width:140px">
            <el-option label="未诊断" :value="0"/>
            <el-option label="AI已诊断" :value="1"/>
            <el-option label="老师已诊断" :value="2"/>
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="query.status" clearable placeholder="全部" style="width:120px">
            <el-option label="待审核" :value="0"/>
            <el-option label="启用" :value="1"/>
            <el-option label="废弃" :value="2"/>
          </el-select>
        </el-form-item>
        <el-form-item label="重复">
          <el-select v-model="query.duplicateOnly" clearable placeholder="全部" style="width:120px">
            <el-option label="仅重复标记" :value="true"/>
            <el-option label="全部" :value="false"/>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card class="table-card">
      <div slot="header" class="card-header">
        <div class="card-header-left"><span>数据列表</span>
          <span class="total-badge">共 <b>{{ total }}</b> 条</span></div>
      </div>
      <el-table :data="list" border stripe v-loading="tableLoading" element-loading-text="加载中...">
        <el-table-column type="index" label="序号" width="70" align="center"/>
        <el-table-column prop="questionType" label="题型" width="120" align="center">
          <template slot-scope="scope">{{ QUESTION_TYPE_MAP[scope.row.questionType] || scope.row.questionType }}</template>
        </el-table-column>
        <el-table-column prop="content" label="题目内容" min-width="240" show-overflow-tooltip/>
        <el-table-column prop="reviewStatus" label="诊断" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="reviewTagType(scope.row.reviewStatus)" size="small" effect="plain">
              {{ reviewLabel(scope.row.reviewStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="aiScore" label="AI评分" width="90" align="center"/>
        <el-table-column prop="status" label="状态" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status===1?'success':(scope.row.status===2?'danger':'warning')" size="small" effect="plain">
              {{ scope.row.status===1?'启用':(scope.row.status===2?'废弃':'待审核') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="duplicateOf" label="重复" width="90" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.duplicateOf" type="warning" size="small" effect="plain">
              基准#{{ scope.row.duplicateOf }}
            </el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="usageCount" label="使用次数" width="90" align="center"/>
        <el-table-column align="center" label="操作" width="200" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="openDetail(scope.row)">查看</el-button>
            <el-button size="mini" type="text" @click="handleReDiagnose(scope.row)" :loading="scope.row._loading">重诊断</el-button>
            <el-button v-if="scope.row.reviewStatus!==2" size="mini" type="text" @click="handleConfirm(scope.row)">老师确认</el-button>
            <el-button v-if="scope.row.duplicateOf" size="mini" type="text" @click="handleResolve(scope.row)">重复处理</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-wrapper" v-if="total > 0">
        <el-pagination background layout="total, sizes, prev, pager, next, jumper"
          @current-change="currentChange" @size-change="sizeChange"
          :page-sizes="[10,20,50,100]" :current-page="query.pageNum" :page-size="query.pageSize" :total="total"/>
      </div>
    </el-card>

    <!-- 详情 / 修订对比 -->
    <el-dialog :close-on-click-modal="false" title="题目详情 / AI修订对比" :visible.sync="detailVisible" width="800px">
      <template v-if="current">
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="题型">{{ QUESTION_TYPE_MAP[current.questionType] || current.questionType }}</el-descriptions-item>
          <el-descriptions-item label="诊断状态">{{ reviewLabel(current.reviewStatus) }}</el-descriptions-item>
          <el-descriptions-item label="AI评分">{{ current.aiScore }}</el-descriptions-item>
          <el-descriptions-item label="命中单词">{{ formatCovered(current.coveredWordIds) }}</el-descriptions-item>
        </el-descriptions>
        <el-divider content-position="left">题目正文</el-divider>
        <pre class="content-block">{{ current.content }}</pre>
        <el-divider content-position="left">AI 修订后（留痕）</el-divider>
        <pre class="content-block revised">{{ current.aiRevisedContent || '（无修订记录）' }}</pre>
        <el-divider content-position="left">中文翻译 / 解析</el-divider>
        <p><b>翻译：</b>{{ current.translation }}</p>
        <p><b>AI修订翻译：</b>{{ current.aiRevisedTranslation || '（无）' }}</p>
        <p><b>解析：</b>{{ current.answerAnalysis }}</p>
        <p><b>AI修订解析：</b>{{ current.aiRevisedAnalysis || '（无）' }}</p>
      </template>
      <div slot="footer">
        <el-button @click="detailVisible=false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  reqLibraryPage, reqConfirmTeacherReview, reqResolveDuplicate, reqReDiagnose
} from '@/api/education/questionLibrary'

export default {
  name: 'QuestionBank',
  data() {
    return {
      QUESTION_TYPE_MAP: {
        1: '高频生词表', 2: 'SuperMemo速记', 3: '例句练习', 4: '好词好句',
        5: '语境语感', 6: '选词填空', 7: '短文填空', 8: '任务型阅读',
        9: '完形填空', 10: '补全对话'
      },
      list: [],
      total: 0,
      tableLoading: false,
      query: { pageNum: 1, pageSize: 10, questionType: null, reviewStatus: null, status: null, duplicateOnly: null },
      detailVisible: false,
      current: null
    }
  },
  created() { this.load() },
  methods: {
    async load() {
      this.tableLoading = true
      try {
        const data = await reqLibraryPage(this.query)
        this.list = data.records || []
        this.total = data.total || 0
      } catch (e) {
        this.$message.error('加载失败')
        console.error(e)
      } finally {
        this.tableLoading = false
      }
    },
    handleSearch() { this.query.pageNum = 1; this.load() },
    handleReset() {
      this.query = { pageNum: 1, pageSize: 10, questionType: null, reviewStatus: null, status: null, duplicateOnly: null }
      this.load()
    },
    currentChange(p) { this.query.pageNum = p; this.load() },
    sizeChange(s) { this.query.pageSize = s; this.load() },
    reviewLabel(s) { return s === 2 ? '老师已诊断' : (s === 1 ? 'AI已诊断' : '未诊断') },
    reviewTagType(s) { return s === 2 ? 'success' : (s === 1 ? 'primary' : 'info') },
    formatCovered(json) {
      if (!json) return '-'
      try { return JSON.parse(json).join(', ') } catch (e) { return json }
    },
    openDetail(row) { this.current = row; this.detailVisible = true },
    async handleConfirm(row) {
      try {
        await reqConfirmTeacherReview(row.id)
        this.$message.success('已确认老师诊断')
        this.load()
      } catch (e) { this.$message.error('操作失败') }
    },
    async handleResolve(row) {
      try {
        await this.$confirm('确认将该题废弃（视为重复）？取消则恢复启用。', '重复处理', { type: 'warning' })
          .then(() => reqResolveDuplicate(row.id, 'discard'))
          .catch(() => reqResolveDuplicate(row.id, 'restore'))
        this.$message.success('处理完成')
        this.load()
      } catch (e) { if (e !== 'cancel') this.$message.error('操作失败') }
    },
    async handleReDiagnose(row) {
      this.$set(row, '_loading', true)
      try {
        await reqReDiagnose(row.id)
        this.$message.success('AI重新诊断完成')
        this.load()
      } catch (e) { this.$message.error('诊断失败') }
      finally { this.$set(row, '_loading', false) }
    }
  }
}
</script>

<style lang="scss" scoped>
.question-bank-page { padding: 16px; }
.card-header { display: flex; justify-content: space-between; align-items: center; }
.header-sub { color: #909399; font-size: 12px; }
.total-badge { margin-left: 12px; color: #606266; font-size: 13px; }
.content-block { background: #f5f7fa; padding: 10px; border-radius: 4px; white-space: pre-wrap; word-break: break-all; max-height: 220px; overflow: auto; }
.content-block.revised { background: #f0f9eb; }
.pagination-wrapper { text-align: right; margin-top: 16px; }
</style>
